/**
 * money.js — единый источник правды по валютам и денежной арифметике (frontend).
 *
 * Принципы (см. multicurrency_plan.md):
 *  - Деньги приходят с бэка как ЦЕЛОЕ число минорных единиц (amount_minor).
 *  - Кол-во знаков минорной единицы (2 или 3) — из справочника по коду валюты.
 *  - Отображение — ISO-код валюты как основной.
 *
 * ВАЖНО: таблица CURRENCIES должна быть идентична backend/utils/money.js.
 * При изменении — править оба файла.
 */

// code → { symbol, minorUnits, nameEn, nameRu }
export const CURRENCIES = {
  AED: { symbol: 'د.إ',  minorUnits: 2, nameEn: 'UAE Dirham',       nameRu: 'Дирхам ОАЭ' },
  SAR: { symbol: '﷼',    minorUnits: 2, nameEn: 'Saudi Riyal',      nameRu: 'Саудовский риял' },
  QAR: { symbol: '﷼',    minorUnits: 2, nameEn: 'Qatari Riyal',     nameRu: 'Катарский риял' },
  KWD: { symbol: 'د.ك',  minorUnits: 3, nameEn: 'Kuwaiti Dinar',    nameRu: 'Кувейтский динар' },
  BHD: { symbol: '.د.ب', minorUnits: 3, nameEn: 'Bahraini Dinar',   nameRu: 'Бахрейнский динар' },
  OMR: { symbol: '﷼',    minorUnits: 3, nameEn: 'Omani Rial',       nameRu: 'Оманский риал' },
  USD: { symbol: '$',    minorUnits: 2, nameEn: 'US Dollar',        nameRu: 'Доллар США' },
  EUR: { symbol: '€',    minorUnits: 2, nameEn: 'Euro',             nameRu: 'Евро' },
  RUB: { symbol: '₽',    minorUnits: 2, nameEn: 'Russian Ruble',    nameRu: 'Рубль' },
  INR: { symbol: '₹',    minorUnits: 2, nameEn: 'Indian Rupee',     nameRu: 'Индийская рупия' },
  PKR: { symbol: '₨',    minorUnits: 2, nameEn: 'Pakistani Rupee',  nameRu: 'Пакистанская рупия' },
};

export const DEFAULT_CURRENCY = 'AED';
export const CURRENCY_CODES = Object.keys(CURRENCIES);

export function isValidCurrency(code) {
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(CURRENCIES, code);
}

export function normCurrency(code) {
  return isValidCurrency(code) ? code : DEFAULT_CURRENCY;
}

export function minorUnits(code) {
  return isValidCurrency(code) ? CURRENCIES[code].minorUnits : 2;
}

export function minorFactor(code) {
  return Math.pow(10, minorUnits(code));
}

/** Мажорная сумма → целое минорных единиц. Для сохранения из форм. */
export function toMinor(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * minorFactor(currency));
}

/** Целое минорных единиц → мажорная сумма. Для полей ввода/расчётов. */
export function fromMinor(amountMinor, currency) {
  const n = Number(amountMinor);
  if (!Number.isFinite(n)) return 0;
  return n / minorFactor(currency);
}

function localeTag(locale) {
  return String(locale || '').startsWith('ru') ? 'ru-RU' : 'en-US';
}

/**
 * Целое минорных единиц → строка с группировкой и ISO-кодом.
 * formatMoney(125000, 'AED', 'ru') → "1 250 AED"
 * withCode=false → только число.
 */
export function formatMoney(amountMinor, currency, locale = 'en', { withCode = true } = {}) {
  const code = normCurrency(currency);
  const major = fromMinor(amountMinor, code);
  const num = new Intl.NumberFormat(localeTag(locale), {
    minimumFractionDigits: 0,
    maximumFractionDigits: minorUnits(code),
  }).format(major);
  return withCode ? `${num} ${code}` : num;
}

/**
 * Форматирует МАЖОРНУЮ сумму (обычное число, напр. 1250.5) с группировкой и кодом.
 * Удобно там, где API отдаёт amount в мажоре. formatMajor(1250.5, 'AED', 'ru') → "1 250,5 AED".
 */
export function formatMajor(amount, currency, locale = 'en', opts = {}) {
  return formatMoney(toMinor(amount, currency), currency, locale, opts);
}

/**
 * Мультивалютный тотал: [{ currency, amountMinor }] → "5 000 AED · 1 200 USD".
 */
export function formatMultiTotal(entries, locale = 'en', { dropZero = false } = {}) {
  const byCur = {};
  for (const e of entries || []) {
    const code = normCurrency(e.currency);
    byCur[code] = (byCur[code] || 0) + Number(e.amountMinor || 0);
  }
  const parts = CURRENCY_CODES
    .filter((c) => byCur[c] != null)
    .filter((c) => !dropZero || byCur[c] !== 0)
    .map((c) => formatMoney(byCur[c], c, locale));
  return parts.length ? parts.join(' · ') : formatMoney(0, DEFAULT_CURRENCY, locale);
}

/**
 * Компактный мультивалютный итог для СВЁРНУТЫХ мест (шапки аккордеонов, тайлы),
 * где полная разбивка «700 AED · 500 USD · 1 000 RUB» не помещается.
 *
 * Показываем ОДНУ главную валюту плюс счётчик «сколько ещё валют». Суммы поперёк
 * валют НЕ складываем: без курсов такое число не значит ничего.
 *
 * Какая валюта главная:
 *   1) preferCurrency (базовая валюта организации) — если по ней есть записи.
 *      Пользователь мыслит в валюте своей орги, и одна и та же Работа не должна
 *      менять «главную» валюту от того, какое начисление оказалось крупнее.
 *   2) иначе — доминирующая (крупнейшая по начислению; бэк отдаёт byCurrency
 *      уже отсортированным). Показать «0 AED» там, где в AED вообще ничего нет,
 *      было бы хуже, чем показать реальную валюту сделки.
 *
 * byCurrency: [{ currency, accruedMinor, receivedMinor }] из services/income.js.
 * Возвращает { text, more, full, currency, codes }:
 *   text     — сумма главной валюты («700 AED»)
 *   more     — сколько валют осталось за кадром (0 — если одна)
 *   full     — полная разбивка для тултипа/раскрытого вида (главная первой)
 *   codes    — коды остальных валют («RUB · USD») для подписи в тайле
 */
export function formatCompactTotal(byCurrency, field = 'accrued', locale = 'en', { withCode = true, preferCurrency = null } = {}) {
  const list = (Array.isArray(byCurrency) ? byCurrency : []).filter(Boolean);
  const key = field === 'received' ? 'receivedMinor' : 'accruedMinor';
  if (!list.length) {
    const code = normCurrency(preferCurrency || DEFAULT_CURRENCY);
    return { text: formatMoney(0, code, locale, { withCode }), more: 0, full: '', currency: code, codes: '' };
  }
  const prefer = preferCurrency ? normCurrency(preferCurrency) : null;
  const idx = prefer ? list.findIndex((c) => normCurrency(c.currency) === prefer) : -1;
  const main = idx >= 0 ? list[idx] : list[0];
  const rest = list.filter((c) => c !== main);
  return {
    text: formatMoney(main[key] || 0, main.currency, locale, { withCode }),
    more: rest.length,
    full: [main, ...rest].map((c) => formatMoney(c[key] || 0, c.currency, locale)).join(' · '),
    currency: normCurrency(main.currency),
    codes: rest.map((c) => normCurrency(c.currency)).join(' · '),
  };
}

/** Опции для <select> валюты: [{ code, label }]. label = "AED · Дирхам ОАЭ". */
export function currencyOptions(locale = 'en') {
  const ru = String(locale || '').startsWith('ru');
  return CURRENCY_CODES.map((code) => ({
    code,
    label: `${code} · ${ru ? CURRENCIES[code].nameRu : CURRENCIES[code].nameEn}`,
  }));
}
